/* eslint-disable no-irregular-whitespace */
import React from 'react';
import { motion } from 'framer-motion';
import ResumeCard from './ResumeCard';

const Experience = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      className="w-full flex flex-col lgl:flex-row gap-10 lgl:gap-20"
    >
      <div>
        <div className="py-6 lgl:py-12 font-titleFont flex flex-col gap-4">
          <h2 className="text-3xl md:text-4xl font-bold">Experience</h2>
        </div>
        <div className="mt-6 lgl:mt-14 w-full h-auto border-l-[6px] border-l-black border-opacity-30 flex flex-col gap-10">
          <ResumeCard
            title="Solo Founder & Lead Developer"
            subTitle="Retour.tech | 2025 - Present"
            result="SaaS Startup"
            des={
              <div className="flex flex-col gap-4">
                <p className="text-gray-400">
                  Built and launched <span className="text-designColor font-bold">Retour</span>, a
                  powerful user feedback tool that lets developers capture insights anywhere in
                  their app. Designed to streamline feedback loops with AI-generated summaries and
                  instant Slack notifications.
                </p>

                {/* Key Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="p-4 bg-[#1e2025] rounded-lg border border-gray-800">
                    <h4 className="text-designColor font-bold mb-2">🚀 Capture & Notify</h4>
                    <p className="text-sm text-gray-400">
                      Users report issues with one click. Teams get instant alerts in Slack with
                      full context.
                    </p>
                  </div>
                  <div className="p-4 bg-[#1e2025] rounded-lg border border-gray-800">
                    <h4 className="text-designColor font-bold mb-2">🧠 AI-Powered Analysis</h4>
                    <p className="text-sm text-gray-400">
                      AI agents analyze feedback to generate concise summaries and actionable
                      intelligence.
                    </p>
                  </div>
                  <div className="p-4 bg-[#1e2024] rounded-lg border border-gray-800">
                    <h4 className="text-designColor font-bold mb-2">🌍 Global Scale</h4>
                    <p className="text-sm text-gray-400">
                      Timezone-aware prompts and multi-tenant support for managing multiple
                      projects.
                    </p>
                  </div>
                  <div className="p-4 bg-[#1e2024] rounded-lg border border-gray-800">
                    <h4 className="text-designColor font-bold mb-2">🔌 Seamless DX</h4>
                    <p className="text-sm text-gray-400">
                      Installs in minutes via NPM. Zero-friction setup for developers.
                    </p>
                  </div>
                </div>

                {/* Technical Implementation / "Demo" */}
                <div className="mt-4 p-4 bg-black rounded-lg border border-gray-800 font-mono text-sm relative group">
                  <div className="absolute top-2 right-2 flex gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <p className="text-gray-500 mb-2"># Initialize Retour SDK</p>
                  <p>
                    <span className="text-purple-400">import</span> {'{ setupFeedback }'}{' '}
                    <span className="text-purple-400">from</span>{' '}
                    <span className="text-green-400">&apos;smart-feedback&apos;</span>;
                  </p>
                  <br />
                  <p>
                    <span className="text-blue-400">await</span>{' '}
                    <span className="text-yellow-400">setupFeedback</span>({'{'}
                  </p>
                  <p className="pl-4">
                    projectId: <span className="text-green-400">&apos;your-project-id&apos;</span>,
                  </p>
                  <p className="pl-4">
                    apiBase:{' '}
                    <span className="text-green-400">&apos;https://www.retour.tech&apos;</span>,
                  </p>
                  <p className="pl-4">
                    variant: <span className="text-green-400">&apos;solid&apos;</span>
                  </p>
                  <p>{'}'});</p>
                </div>

                <p className="text-gray-400 mt-2 italic">
                  &quot;From code to connection. Retour transforms raw user feedback into actionable
                  intelligence.&quot;
                </p>

                {/* Floating Feedback Cards */}
                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 justify-center">
                  {[
                    // 2 Lines
                    {
                      name: 'Aarav Patel',
                      role: 'Indie Developer',
                      quote:
                        'Retour makes collecting user feedback effortless. Clean UI, powerful insights, and zero friction.',
                    },
                    // 5 Lines
                    {
                      name: 'Sarah Jenkins',
                      role: 'Software Engineer',
                      quote:
                        "We plugged in Retour and immediately started getting actionable feedback. It’s crazy how much we missed before—now every user voice is captured, categorized, and prioritized automatically. It's completely transformed our sprint planning process.",
                    },
                    // 4 Lines
                    {
                      name: 'Avi Ben-David',
                      role: 'SaaS Builder',
                      quote:
                        'The setup was deceptively simple. Within minutes we had a beautiful dashboard live. But the real value is the feedback quality—it actually drives our product roadmap decisions now.',
                    },
                    // 3 Lines
                    {
                      name: 'Michael Ross',
                      role: 'Senior SDE',
                      quote:
                        "Retour turns the chaotic noise of raw user opinions into clear, usable insights in minutes. It's like having a dedicated data analyst on the team.",
                    },
                    // 2 Lines
                    {
                      name: 'Lucas Dubois',
                      role: 'Frontend Developer',
                      quote:
                        'The fastest way we’ve found to understand what users really think. Simple, effective, and beautiful.',
                    },
                    // 3 Lines
                    {
                      name: 'Klaus Weber',
                      role: 'DevOps Engineer',
                      quote:
                        'From feedback collection to summaries—Retour handles everything smoothly. The integration with our existing tools was flawless and instant.',
                    },
                    // 2 Lines
                    {
                      name: 'Priya Sharma',
                      role: 'Indie Hacker',
                      quote:
                        "A must-have tool for teams who care about user experience. I can't imagine building without it now.",
                    },
                    // 5 Lines
                    {
                      name: 'Noa Peretz',
                      role: 'Product Marketing',
                      quote:
                        'Retour helped us spot a critical bug in our checkout flow before it became a real problem. The instant Slack alert saved us thousands in lost revenue that day alone. It literally paid for itself in the first week of use.',
                    },
                    // 4 Lines
                    {
                      name: 'Markus Ray',
                      role: 'Software Developer',
                      quote:
                        "Lightweight, powerful, and built exactly for modern SaaS teams. It gives us the confidence that we're building exactly what our users need, not just what we guess they want.",
                    },
                    // 2 Lines
                    {
                      name: 'Hanna Schmidt',
                      role: 'Full Stack Dev',
                      quote:
                        'Finally, a feedback tool that feels simple but delivers real value. Highly recommended for dev teams.',
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      whileHover={{
                        y: -5,
                        // Cyan glow effect using designColor (#00f3ff)
                        boxShadow: '0 0 25px rgba(0, 243, 255, 0.15)',
                        borderColor: '#00f3ff',
                      }}
                      className="p-6 bg-white rounded-lg border border-gray-200 shadow-xl w-full cursor-default relative overflow-hidden group transition-all duration-300"
                    >
                      <div className="absolute top-0 right-0 w-20 h-20 bg-designColor/5 blur-2xl rounded-full group-hover:bg-designColor/10 transition-all"></div>

                      <div className="flex items-center gap-4 mb-4">
                        <div>
                          <h5 className="text-gray-900 font-bold text-sm group-hover:text-designColor transition-colors text-lg">
                            {item.name}
                          </h5>
                          <p className="text-xs text-gray-500">{item.role}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic font-medium leading-6">
                        &quot;{item.quote}&quot;
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            }
          />
          {/* Wells Fargo Software Developer Intern */}
          <ResumeCard
            title="Wells Fargo Software Developer "
            // subTitle=" - February 2024"
            result="Remote"
            des={
              <>
                <ul className="list-disc ml-5">
                  <li>
                    Designed and developed a robust system for managing financial portfolios,
                    boosting efficiency with a 20% reduction in response times and improving data
                    integrity through optimized ERD implementations.
                  </li>
                  <li>
                    Collaborated across teams to align business and technical goals, ensuring
                    seamless integration and the implementation of features that met both user needs
                    and business requirements.
                  </li>
                  <li>
                    Contributed to automation efforts, reducing manual tasks and improving project
                    timelines by integrating automated data reporting tools into the platform.
                  </li>
                </ul>
              </>
            }
          />
          {/* Lyft Back-End Engineering Intern */}
          <ResumeCard
            title="Lyft Back-End Engineering "
            // subTitle=" - November 2023"
            result="Remote"
            des={
              <>
                <ul className="list-disc ml-5">
                  <li>
                    Refined complex legacy code, implementing efficient algorithms and improving
                    processing times by 30%, which significantly reduced system latency and
                    optimized performance.
                  </li>
                  <li>
                    Collaborated in designing modular architecture, cutting down technical debt by
                    40%, and ensuring scalability and maintainability across backend systems.
                  </li>
                  <li>
                    Introduced best practices for code reviews and collaboration, enhancing team
                    efficiency and code quality through improved communication and technical
                    solutions.
                  </li>
                </ul>
              </>
            }
          />
          {/* Skyscanner Front-End Intern */}
          <ResumeCard
            title="Skyscanner Front-End "
            // subTitle=" - August 2023"
            result="Remote"
            des={
              <>
                <ul className="list-disc ml-5">
                  <li>
                    Developed a high-performance React-based web application, enhancing user
                    experience and increasing engagement by 25% through optimized load times and
                    responsive design.
                  </li>
                  <li>
                    Led automation of tests and ensured cross-browser compatibility, further
                    elevating site performance and stability.
                  </li>
                  <li>
                    Collaborated with UX/UI teams to implement design changes that led to a 15%
                    improvement in user satisfaction and interaction with the platform.
                  </li>
                </ul>
              </>
            }
          />
        </div>
      </div>
    </motion.div>
  );
};

export default Experience;
